import { useEffect, useMemo, useRef, useState } from "react";
import { auth, db, storage } from "../../firebaseConfig";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import toast from "react-hot-toast";

const STAGES = [
  { key: "EXCAVATION", label: "Excavation / Site Work" },
  { key: "FOOTING", label: "Footing" },
  { key: "PLINTH_BEAM", label: "Plinth Beam" },
  { key: "COLUMNS_BEAMS", label: "Column & Beam Work" },
  { key: "BRICK_MASONRY", label: "Brick Masonry Work" },
  { key: "PLASTER", label: "Plaster Work" },
  { key: "PEB_INSPECTION", label: "PEB Inspection" },
  { key: "PEB_INSTALLATION", label: "PEB Installation" },
  { key: "COLOUR_WORK", label: "Colour Work" },
  { key: "LOGO_BRANDING", label: "Logo Design & Branding" },
  { key: "FINISHING", label: "Finishing" },
  { key: "COMPLETED", label: "Completed" },
];

function Badge({ status }) {
  const s = String(status || "PENDING").toUpperCase();

  const cls =
    s === "COMPLETED"
      ? "bg-emerald-100 text-emerald-700 border-emerald-300"
      : s === "IN_PROGRESS"
      ? "bg-blue-100 text-blue-700 border-blue-300"
      : "bg-amber-100 text-amber-700 border-amber-300";

  return (
    <span className={`rounded-full border px-2 py-1 text-[10px] font-black ${cls}`}>
      {s.replace("_", " ")}
    </span>
  );
}

export default function LoaManager() {
  const fileInputRef = useRef(null);

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [stageKey, setStageKey] = useState("EXCAVATION");
  const [status, setStatus] = useState("IN_PROGRESS");
  const [startDate, setStartDate] = useState("");
  const [completeDate, setCompleteDate] = useState("");
  const [remark, setRemark] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const uid = auth.currentUser?.uid || "";

  const selectedProject = useMemo(() => {
    return projects.find((p) => p.id === selectedProjectId) || null;
  }, [projects, selectedProjectId]);

  const selectedStageData = selectedProject?.constructionTimeline?.[stageKey] || {};
  const selectedStageFiles = Array.isArray(selectedStageData.files)
    ? selectedStageData.files
    : [];

  const calculateProgress = (timeline = {}, nextKey = "", nextStatus = "") => {
    const merged = {
      ...timeline,
      [nextKey]: {
        ...(timeline[nextKey] || {}),
        status: nextStatus,
      },
    };

    const done = STAGES.filter(
      (s) => String(merged?.[s.key]?.status || "").toUpperCase() === "COMPLETED"
    ).length;

    return Math.round((done / STAGES.length) * 100);
  };

  const addDocsToMap = (snap, map, tenderData = null) => {
    snap.forEach((d) => {
      const data = d.data();

      map.set(d.id, {
        id: d.id,
        ...data,
        _linkedTender: tenderData || null,
      });
    });
  };

  const loadProjects = async () => {
    try {
      setLoading(true);

      if (!uid) {
        toast.error("Contractor login not found.");
        setLoading(false);
        return;
      }

      const map = new Map();

      const directQueries = [
        query(collection(db, "godown_projects"), where("contractorUid", "==", uid)),
        query(collection(db, "godown_projects"), where("tenderAwardVendorUid", "==", uid)),
        query(collection(db, "godown_projects"), where("awardedVendorUid", "==", uid)),
      ];

      for (const qRef of directQueries) {
        const snap = await getDocs(qRef);
        addDocsToMap(snap, map);
      }

      const tenderSnap = await getDocs(
        query(collection(db, "tenders"), where("awardedVendorUid", "==", uid))
      );

      for (const tenderDoc of tenderSnap.docs) {
        const t = tenderDoc.data();
        const tenderId = tenderDoc.id;

        const workflowIds = [];

        if (t.workflowProjectId) workflowIds.push(t.workflowProjectId);

        if (Array.isArray(t.linkedWorkflowProjectIds)) {
          t.linkedWorkflowProjectIds.forEach((id) => {
            if (id && !workflowIds.includes(id)) workflowIds.push(id);
          });
        }

        if (Array.isArray(t.tenderUnits)) {
          t.tenderUnits.forEach((u) => {
            if (u?.workflowProjectId && !workflowIds.includes(u.workflowProjectId)) {
              workflowIds.push(u.workflowProjectId);
            }
          });
        }

        const tenderData = {
          tenderId,
          contractorUid: uid,
          contractorName: t.awardedVendorName || t.vendorName || "",
          awardAmount: t.awardedAmount || t.awardedValue || t.amount || 0,
          loaGenerateDate: t.loaGenerateDate || "",
          workStartDate: t.workStartDate || "",
          workDurationDays: t.workDurationDays || 0,
        };

        const byTenderIdSnap = await getDocs(
          query(collection(db, "godown_projects"), where("tenderId", "==", tenderId))
        );
        addDocsToMap(byTenderIdSnap, map, tenderData);

        const byLinkedTenderIdSnap = await getDocs(
          query(collection(db, "godown_projects"), where("linkedTenderId", "==", tenderId))
        );
        addDocsToMap(byLinkedTenderIdSnap, map, tenderData);

        for (const wfId of workflowIds) {
          const gpSnap = await getDocs(
            query(collection(db, "godown_projects"), where("workflowProjectId", "==", wfId))
          );
          addDocsToMap(gpSnap, map, tenderData);
        }
      }

      const list = Array.from(map.values()).sort((a, b) =>
        String(a.pacsName || a.ownerCompany || a.applicationId || "").localeCompare(
          String(b.pacsName || b.ownerCompany || b.applicationId || "")
        )
      );

      setProjects(list);

      if (list.length && !selectedProjectId) {
        setSelectedProjectId(list[0].id);
      }

      if (!list.length) {
        console.warn("No construction project found for contractor uid:", uid);
      }
    } catch (e) {
      console.error(e);
      toast.error("Construction projects load failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  useEffect(() => {
    if (!selectedProject) return;

    const old = selectedProject?.constructionTimeline?.[stageKey] || {};

    setStartDate(old.startDate || old.date || "");
    setCompleteDate(old.completeDate || old.endDate || "");
    setRemark(old.note || old.remark || "");
    setStatus(old.status || "IN_PROGRESS");
    setFiles([]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [selectedProject, stageKey]);

  const makeSafeFileName = (fileName = "file") => {
    const ext = fileName.includes(".") ? fileName.split(".").pop() : "";
    const base = fileName
      .replace(/\.[^/.]+$/, "")
      .replace(/[^a-zA-Z0-9_-]/g, "_")
      .slice(0, 80);

    return `${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${base}${
      ext ? `.${ext}` : ""
    }`;
  };

  const uploadStageFiles = async () => {
    if (!files.length) return [];

    if (!selectedProjectId || !stageKey) {
      throw new Error("Project or stage missing for file upload.");
    }

    const uploaded = [];

    for (const file of files) {
      const safeName = makeSafeFileName(file.name);
      const path = `construction_progress/${selectedProjectId}/${stageKey}/${safeName}`;
      const sRef = ref(storage, path);

      await uploadBytes(sRef, file, {
        contentType: file.type || "application/octet-stream",
      });

      const url = await getDownloadURL(sRef);

      uploaded.push({
        name: file.name,
        fileName: safeName,
        path,
        url,
        type: file.type || "",
        size: file.size || 0,
        uploadedBy: uid,
        uploadedAt: new Date().toISOString(),
      });
    }

    return uploaded;
  };

  const saveProgress = async () => {
    if (!selectedProject) {
      toast.error("Please select project.");
      return;
    }

    if (!startDate) {
      toast.error("Please select start date.");
      return;
    }

    try {
      setSaving(true);

      const oldTimeline = selectedProject.constructionTimeline || {};
      const oldFiles = Array.isArray(oldTimeline?.[stageKey]?.files)
        ? oldTimeline[stageKey].files
        : [];

      const uploadedFiles = await uploadStageFiles();
      const progressPercent = calculateProgress(oldTimeline, stageKey, status);
      const linkedTender = selectedProject._linkedTender || {};

      await updateDoc(doc(db, "godown_projects", selectedProject.id), {
        contractorUid: selectedProject.contractorUid || uid,
        tenderAwardVendorUid: selectedProject.tenderAwardVendorUid || uid,
        awardedVendorUid: selectedProject.awardedVendorUid || uid,
        contractorName:
          selectedProject.contractorName ||
          linkedTender.contractorName ||
          "Contractor",

        [`constructionTimeline.${stageKey}`]: {
          ...(oldTimeline?.[stageKey] || {}),
          status,
          startDate,
          completeDate: completeDate || "",
          endDate: completeDate || "",
          date: completeDate || startDate,
          note: remark || "",
          remark: remark || "",
          files: [...oldFiles, ...uploadedFiles],
          updatedBy: uid,
          updatedAt: serverTimestamp(),
        },

        constructionOverallProgress: progressPercent,
        constructionStatus:
          progressPercent >= 100 || stageKey === "COMPLETED"
            ? "COMPLETED"
            : "IN_PROGRESS",

        contractorLastProgressAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      toast.success(
        uploadedFiles.length
          ? `${uploadedFiles.length} file upload + construction progress saved.`
          : "Construction progress saved."
      );

      setFiles([]);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      await loadProjects();
    } catch (e) {
      console.error("Progress save failed:", e);
      toast.error(e?.message || "Photo save failed. Storage rules/path check karo.");
    } finally {
      setSaving(false);
    }
  };

  const deleteStageFile = async (fileToDelete, indexToDelete) => {
    if (!selectedProject) {
      toast.error("Project select nahi hai.");
      return;
    }

    const ok = window.confirm("Ye photo/file delete karni hai?");
    if (!ok) return;

    try {
      setSaving(true);

      const oldTimeline = selectedProject.constructionTimeline || {};
      const oldStage = oldTimeline?.[stageKey] || {};
      const oldFiles = Array.isArray(oldStage.files) ? oldStage.files : [];

      const updatedFiles = oldFiles.filter((_, i) => i !== indexToDelete);

      if (fileToDelete?.path) {
        try {
          await deleteObject(ref(storage, fileToDelete.path));
        } catch (storageErr) {
          console.warn("Storage delete failed, Firestore list update continuing:", storageErr);
        }
      }

      await updateDoc(doc(db, "godown_projects", selectedProject.id), {
        [`constructionTimeline.${stageKey}`]: {
          ...oldStage,
          files: updatedFiles,
          updatedBy: uid,
          updatedAt: serverTimestamp(),
        },
        updatedAt: serverTimestamp(),
      });

      toast.success("Photo/File deleted.");
      await loadProjects();
    } catch (e) {
      console.error("File delete failed:", e);
      toast.error("File delete failed.");
    } finally {
      setSaving(false);
    }
  };

  const timeline = selectedProject?.constructionTimeline || {};
  const overall = Number(selectedProject?.constructionOverallProgress || 0);

  return (
    <div className="space-y-5">
      <div className="rounded-3xl bg-slate-950 px-5 py-5 text-white">
        <div className="text-[11px] font-black uppercase tracking-[0.25em] text-emerald-300">
          Project Execution
        </div>
        <div className="mt-2 text-xl font-black">Construction Progress Update</div>
        <div className="mt-1 text-sm text-slate-300">
          Stage wise start date, end date, complete date, remark and files update karo.
          Ye stakeholder construction tab me live update hoga.
        </div>
      </div>

      <div className="rounded-2xl border bg-white p-5 shadow-sm">
        {loading ? (
          <div className="text-sm font-semibold text-slate-500">
            Loading assigned projects...
          </div>
        ) : projects.length === 0 ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-bold text-amber-700">
            Abhi aapko koi construction project assign nahi hai. PMC LOA panel se
            contractor assignment sync karo ya godown_projects me contractorUid set karo.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <label className="text-[11px] font-black uppercase text-slate-500">
                Select Project
              </label>
              <select
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-3 text-sm font-bold"
              >
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.pacsName ||
                      p.ownerCompany ||
                      p.applicationId ||
                      p._linkedTender?.contractorName ||
                      p.id}{" "}
                    - {p.district || p.city || "District"}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-[11px] font-black uppercase text-slate-500">
                Overall Progress
              </label>
              <div className="mt-1 rounded-xl border bg-slate-50 px-4 py-3">
                <div className="text-2xl font-black text-emerald-700">{overall}%</div>
                <div className="mt-2 h-2 rounded-full bg-slate-200">
                  <div
                    className="h-2 rounded-full bg-emerald-600"
                    style={{ width: `${overall}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {selectedProject && (
        <>
          <div className="rounded-2xl border bg-white p-5 shadow-sm">
            <div className="grid gap-4 md:grid-cols-5">
              <div>
                <label className="text-[11px] font-black uppercase text-slate-500">
                  Stage
                </label>
                <select
                  value={stageKey}
                  onChange={(e) => setStageKey(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-3 text-sm font-bold"
                >
                  {STAGES.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[11px] font-black uppercase text-slate-500">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-3 text-sm font-bold"
                >
                  <option value="PENDING">Pending</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="COMPLETED">Completed</option>
                </select>
              </div>

              <div>
                <label className="text-[11px] font-black uppercase text-slate-500">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-3 text-sm font-bold"
                />
              </div>

              <div>
                <label className="text-[11px] font-black uppercase text-slate-500">
                  End / Complete Date
                </label>
                <input
                  type="date"
                  value={completeDate}
                  onChange={(e) => setCompleteDate(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-3 text-sm font-bold"
                />
              </div>

              <div>
                <label className="text-[11px] font-black uppercase text-slate-500">
                  Photos / Files
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={(e) => setFiles(Array.from(e.target.files || []))}
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-xs font-bold"
                />
                {files.length > 0 && (
                  <div className="mt-1 text-[11px] font-bold text-emerald-700">
                    {files.length} file selected
                  </div>
                )}
              </div>

              <div className="md:col-span-5">
                <label className="text-[11px] font-black uppercase text-slate-500">
                  Remark / Work Note
                </label>
                <textarea
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  rows={4}
                  placeholder="Aaj ka work detail..."
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-3 text-sm font-semibold"
                />
              </div>
            </div>

            {selectedStageFiles.length > 0 && (
              <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-3">
                <div className="text-xs font-black text-emerald-800">
                  Current Stage Uploaded Files
                </div>

                <div className="mt-3 grid gap-3 md:grid-cols-3">
                  {selectedStageFiles.map((f, idx) => (
                    <div
                      key={`${f.url || f.path || idx}`}
                      className="rounded-2xl border bg-white p-3 shadow-sm"
                    >
                      {String(f.type || "").startsWith("image/") && f.url ? (
                        <img
                          src={f.url}
                          alt={f.name || `File ${idx + 1}`}
                          className="h-24 w-full rounded-xl object-cover"
                        />
                      ) : (
                        <div className="grid h-24 place-items-center rounded-xl bg-slate-100 text-xs font-bold text-slate-500">
                          FILE
                        </div>
                      )}

                      <div className="mt-2 truncate text-[11px] font-bold text-slate-700">
                        {f.name || `File ${idx + 1}`}
                      </div>

                      <div className="mt-2 flex gap-2">
                        <a
                          href={f.url}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-full bg-emerald-600 px-3 py-1 text-[11px] font-black text-white"
                        >
                          View
                        </a>

                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => deleteStageFile(f, idx)}
                          className="rounded-full bg-red-600 px-3 py-1 text-[11px] font-black text-white disabled:bg-slate-300"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              type="button"
              disabled={saving}
              onClick={saveProgress}
              className="mt-4 rounded-full bg-emerald-600 px-6 py-3 text-xs font-black text-white hover:bg-emerald-700 disabled:bg-slate-300"
            >
              {saving ? "Uploading / Saving..." : "Save Construction Progress"}
            </button>
          </div>

          <div className="rounded-2xl border bg-white p-5 shadow-sm">
            <div className="mb-4 text-sm font-black text-slate-900">
              Stage-wise Current Status
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {STAGES.map((s) => {
                const data = timeline[s.key] || {};
                const fileCount = Array.isArray(data.files) ? data.files.length : 0;

                return (
                  <div key={s.key} className="rounded-2xl border border-slate-200 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-black text-slate-900">
                          {s.label}
                        </div>
                        <div className="mt-1 text-[11px] text-slate-500">
                          Start: {data.startDate || data.date || "—"}
                        </div>
                        <div className="mt-1 text-[11px] text-slate-500">
                          End: {data.completeDate || data.endDate || "—"}
                        </div>
                      </div>

                      <Badge status={data.status || "PENDING"} />
                    </div>

                    {(data.note || data.remark) && (
                      <div className="mt-3 rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
                        {data.note || data.remark}
                      </div>
                    )}

                    {fileCount > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {data.files.slice(0, 4).map((f, idx) => (
                          <a
                            key={`${f.url || f.path || idx}`}
                            href={f.url}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-bold text-emerald-700 border border-emerald-200"
                          >
                            {f.name || `File ${idx + 1}`}
                          </a>
                        ))}

                        {fileCount > 4 && (
                          <span className="text-[11px] font-bold text-slate-500">
                            +{fileCount - 4} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}